import { Component, inject, input, OnInit, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { SnrDestination, SnrDestinationResult } from '../../../../shared/models/generated/ziroModels';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import {
  ZiroNavsetComponent,
  ZiroNavsetConfig,
  ZiroNavsetItemConfig,
} from '../../../../shared/ziro-navset/ziro-navset.component';
import { HtmlInputType, InputSize, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { SnrProfileResource } from '../../../../self-serve/resources/snr-profile.resource';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

interface SnrDestinationForm {
  destinations: SnrDestination[];
  snrNavset: {
    destinations: SnrDestination[];
  };
}

@Component({
  selector: 'ziro-snr-destinations-form',
  templateUrl: './snr-destinations-form.component.html',
  standalone: false,
})
export class SnrDestinationsFormComponent
  extends ZiroFormAbstractDirective<SnrDestination[], SnrDestinationForm>
  implements OnInit
{
  protected _ziroFormStateService: ZiroFormStateService;
  private translateService = inject(TranslateService);
  private snrProfileResource = inject(SnrProfileResource);

  @ViewChild(ZiroNavsetComponent) snrNavset: ZiroNavsetComponent;
  readonly cucmServerId = input<number>();
  readonly initialSnrDestinations = input<SnrDestination[]>();

  protected formConfig: ZiroFormConfig;
  private _ziroIcons = ZiroIcons;
  private _destinationInUseMessage = '';
  private _validators = {
    destinationsRegexValidator: (destNumInput: string) =>
      !destNumInput || /^.+@.+$/.test(destNumInput) || /^[0-9*#+]{1,24}$/i.test(destNumInput)
        ? ZiroFormsValidationState.VALID
        : ZiroFormsValidationState.INVALID,
    destinationsSyncValidator: (destNumInput: string, id: string) => {
      let matchCurrentSnr;

      if (this.formData.snrNavset.destinations) {
        matchCurrentSnr = this.formData.snrNavset.destinations.find(
          (destination: SnrDestination) => destination.destinationNumber === destNumInput && destination.id !== id,
        );
      }

      if (matchCurrentSnr) {
        this._destinationInUseMessage = this.translateService.instant('tkey;snr.snrdestination.destination.in_use', {
          destinationName: matchCurrentSnr.destinationName,
        });
        return ZiroFormsValidationState.INVALID;
      }
      return ZiroFormsValidationState.VALID;
    },
    destinationAsyncValidator: (destNumInput: string): Observable<ZiroFormsValidationState> => {
      return this.snrProfileResource.searchSnrDestinations(destNumInput, this.cucmServerId()).pipe(
        map((destinations: SnrDestinationResult[]) => {
          const initialIds = this.initialSnrDestinations().map((dest) => dest.id);

          const matchOtherUsers = destinations.find(
            (destination: SnrDestinationResult) =>
              destination.ref.destination === destNumInput && !initialIds.includes(destination.ref.id),
          );

          if (matchOtherUsers) {
            this._destinationInUseMessage = this.translateService.instant(
              'tkey;snr.snrdestination.destination.in_use',
              {
                destinationName: matchOtherUsers.ref.name,
              },
            );
            return ZiroFormsValidationState.INVALID;
          }

          return ZiroFormsValidationState.VALID;
        }),
      );
    },
  };

  ngOnInit(): void {
    this._initFormConfig();
  }

  toFormData = (entity: SnrDestination[]): SnrDestinationForm => {
    const entityClone = cloneDeep(entity);
    const formData: any = {};

    if (entityClone.length) {
      formData.destinations = [];
    }
    entityClone.forEach((destination) => {
      formData.destinations.push({
        ...destination,
      });
    });

    return {
      destinations: entityClone,
      snrNavset: formData,
    };
  };

  toEntity = (formData: SnrDestinationForm): SnrDestination[] => {
    const formDataClone = cloneDeep(formData);
    const initialDestinations = formDataClone.destinations;
    const navsetDestinations = formDataClone.snrNavset.destinations;

    let finalDestinations: SnrDestination[] = [];
    // Previous destinations existed
    if (initialDestinations?.length && navsetDestinations?.length) {
      const deletedDestinations = initialDestinations.filter((destination) => {
        return !navsetDestinations.find((navsetDestination) => navsetDestination.id === destination.id);
      });
      deletedDestinations.forEach((deletedDestination) => (deletedDestination.destinationNumber = null));
      finalDestinations = [...deletedDestinations, ...navsetDestinations];
    }
    // Previous destinations existed, but all have been deleted now
    if (initialDestinations?.length && !navsetDestinations?.length) {
      const allFormerDestinationsRemoved = initialDestinations.map((destination) => {
        destination.destinationNumber = '';
        return destination;
      });
      finalDestinations = [...allFormerDestinationsRemoved];
    }
    // No previous destination existed, just adding new ones
    if (!initialDestinations?.length && navsetDestinations?.length) {
      finalDestinations = navsetDestinations;
    }

    return finalDestinations;
  };

  submit(): Observable<any> {
    return this.parentSubmit$();
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        snrNavset: {
          componentConfig: new ZiroNavsetConfig({
            dropdownLabel: 'tkey;snr.snrdestination.add_button.label',
            dropdownTooltip: 'tkey;snr.snrdestination.add_button.tooltip',
            emptyMessage: 'tkey;snr.snrdestination.no_destinations',
            navsetItemsConfig: this._getDestinationsConfig(),
            allowDuplicates: true,
            isOneClickAddDropdown: true,
          }),
        },
      },
    };
  }

  private _getDestinationsConfig(): ZiroNavsetItemConfig {
    const destinationsNavsetFields: ZiroNavsetItemConfig = {
      destinations: {
        label: 'New Destination',
        labelFieldId: 'destinationName',
        defaultValue: () => ({
          destinationName: '',
          destinationNumber: '',
          delayBeforeRingingInSeconds: 4,
          answerTooSoonTimerInSeconds: 1.5,
          answerTooLateTimerInSeconds: 19,
          id: Date.now().toString(),
        }),
        fields: [
          {
            label: 'tkey;snr.snrdestination.name.label',
            dataAutomation: 'destinationName',
            required: true,
            componentConfig: new ZiroTextConfig({
              htmlInputType: HtmlInputType.TEXT,
              inputSize: InputSize.SM,
            }),
            validation: [
              {
                validator: (val: string) => {
                  return val.length > 50 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
                },
                message: this.translateService.instant('tkey;validators.global.error.maxlength', { maxlength: 50 }),
              },
            ],
          },
          {
            label: 'tkey;snr.snrdestination.destinationnumber.label',
            dataAutomation: 'destinationNumber',
            required: true,
            componentConfig: new ZiroTextConfig({
              htmlInputType: HtmlInputType.TEXT,
              inputSize: InputSize.SM,
            }),
            validation: [
              {
                validator: this._validators.destinationsRegexValidator,
                message: 'tkey;snr.snrdestination.destination.invalid_destination',
              },
              {
                validator: this._validators.destinationsSyncValidator,
                message: () => this._destinationInUseMessage,
                injectValuesFromFields: ['id'],
              },
              {
                validator: this._validators.destinationAsyncValidator,
                message: () => this._destinationInUseMessage,
              },
            ],
          },
          {
            label: 'tkey;snr.snrdestination.delay_ring.label',
            dataAutomation: 'delayBeforeRingingInSeconds',
            required: true,
            componentConfig: new ZiroTextConfig({
              htmlInputType: HtmlInputType.NUMBER,
              inputSize: InputSize.SM,
              min: 0,
              max: 30,
            }),
            labelToolTipIconClass: `${this._ziroIcons.TOOLTIP} tooltip-icon`,
            labelToolTipText: 'tkey;snr.snr_destination.delay_before_ring_timer.tooltip',
            validation: [
              {
                validator: (val: string) => {
                  return Number(val) >= 0 && Number(val) <= 30
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                },
                message: () => {
                  return {
                    content: 'tkey;snr.snrdestination.destination.range.text',
                    params: { min: 0, max: 30 },
                  };
                },
              },
            ],
          },
          {
            label: 'tkey;snr.snr_destination.answer_too_soon_timer.label',
            dataAutomation: 'answerTooSoonTimerInSeconds',
            componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
            labelToolTipIconClass: `${this._ziroIcons.TOOLTIP} tooltip-icon`,
            labelToolTipText: 'tkey;snr.snr_destination.answer_too_soon_timer.tooltip',
            required: true,
            validation: [
              {
                validator: (val: string) => {
                  return Number(val) >= 0 && Number(val) <= 10
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                },
                message: () => {
                  return {
                    content: 'tkey;snr.snrdestination.destination.range.text',
                    params: { min: 0, max: 10 },
                  };
                },
              },
            ],
          },
          {
            label: 'tkey;snr.snr_destination.answer_too_late_timer.label',
            dataAutomation: 'answerTooLateTimerInSeconds',
            componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
            labelToolTipIconClass: `${this._ziroIcons.TOOLTIP} tooltip-icon`,
            labelToolTipText: 'tkey;snr.snr_destination.answer_too_late_timer.tooltip',
            required: true,
            validation: [
              {
                validator: (val: string) => {
                  return Number(val) === 0 || (Number(val) >= 10 && Number(val) <= 300)
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                },
                message: () => {
                  return {
                    content: 'tkey;snr.snrdestination.destination.answer_too_late.validation.text',
                    params: { min: 0, start: 10, end: 300 },
                  };
                },
              },
            ],
          },
          {
            label: 'id',
            dataAutomation: 'id',
            componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
            labelToolTipIconClass: `${this._ziroIcons.TOOLTIP} tooltip-icon`,
            hidden: () => true,
          },
        ],
      },
    };

    return { ...destinationsNavsetFields };
  }
}
